import { ClipboardCheck, RefreshCw, SlidersHorizontal } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";

import { PageHeader } from "@/components/shared/page-header";
import { Button } from "@/components/ui/button";

import { EditMinStockDialog } from "./edit-min-stock-dialog";
import { StockCardDialog } from "./stock-card-dialog";
import { StockKpiCards } from "./stock-kpi-cards";
import { StockTable } from "./stock-table";
import { useEditMinStock } from "./use-edit-min-stock";
import { useStocks } from "./use-stocks";

import type { StockItem } from "./types";

export function StockList() {
  const {
    filteredItems,
    query,
    setQuery,
    typeFilter,
    setTypeFilter,
    statusFilter,
    setStatusFilter,
    handleSelectStatusFilter,
    handleResetDemo,
    handleUpdateItemMinStock,
    totalAssetValue,
    inStockCount,
    lowStockCount,
    outOfStockCount,
  } = useStocks();

  const [cardItem, setCardItem] = useState<StockItem | null>(null);
  const [isCardOpen, setIsCardOpen] = useState(false);

  function handleViewCard(item: StockItem) {
    setCardItem(item);
    setIsCardOpen(true);
  }

  const {
    open: isEditDialogOpen,
    setOpen: setEditDialogOpen,
    editingItem,
    form: editForm,
    isSubmitting: isSubmittingEdit,
    openDialog: openEditMinStock,
    onSubmit: onEditMinStockSubmit,
    setPreset,
    adjustPreset,
  } = useEditMinStock(handleUpdateItemMinStock);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Daftar Stok"
        description="Monitoring saldo persediaan bahan baku dan produk siap jual di toko secara real-time."
        action={
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleResetDemo}
              className="text-xs text-muted-foreground min-h-[40px] sm:min-h-0"
              title="Reset filter pencarian dan status"
            >
              <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
              Reset Filter
            </Button>
            <Button
              asChild
              variant="outline"
              size="sm"
              className="gap-1.5 text-xs min-h-[40px] sm:min-h-0"
            >
              <Link to="/stock-adjustments">
                <SlidersHorizontal className="h-4 w-4" />
                <span>Penyesuaian Stok</span>
              </Link>
            </Button>
            <Button
              asChild
              size="sm"
              className="gap-1.5 text-xs min-h-[40px] sm:min-h-0"
            >
              <Link to="/stock-opname/raw-materials">
                <ClipboardCheck className="h-4 w-4" />
                <span>Stock Opname</span>
              </Link>
            </Button>
          </div>
        }
      />

      {/* KPI Cards */}
      <StockKpiCards
        totalAssetValue={totalAssetValue}
        inStockCount={inStockCount}
        lowStockCount={lowStockCount}
        outOfStockCount={outOfStockCount}
        activeStatusFilter={statusFilter}
        onSelectStatusFilter={handleSelectStatusFilter}
      />

      {/* Stock Table */}
      <StockTable
        items={filteredItems}
        query={query}
        onQueryChange={setQuery}
        typeFilter={typeFilter}
        onTypeFilterChange={setTypeFilter}
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
        onEditMinStock={openEditMinStock}
        onViewStockCard={handleViewCard}
      />

      {/* Quick Edit Min Stock Dialog */}
      <EditMinStockDialog
        open={isEditDialogOpen}
        onOpenChange={setEditDialogOpen}
        item={editingItem}
        form={editForm}
        isSubmitting={isSubmittingEdit}
        onSubmit={onEditMinStockSubmit}
        setPreset={setPreset}
        adjustPreset={adjustPreset}
      />

      {/* Stock Card History Dialog */}
      <StockCardDialog
        open={isCardOpen}
        onOpenChange={setIsCardOpen}
        item={cardItem}
      />
    </div>
  );
}
